/**
 * Web API Client
 * Adapted from desktop client for browser environment
 */

import { createLogger } from '@ctrl-enter/tools/utils';
import { apiContract } from '@ctrl-enter/types/contracts/service-gateway';
import { initTsrReactQuery } from '@ts-rest/react-query/v5';
import { getValidAccessToken } from '../auth/token-manager';
import { API_PUBLIC_KEY } from '../env';

const logger = createLogger('web:api-client');

// API base URL - use environment variable or default to localhost
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

logger.info('🏠 [APIClient] Initializing with base URL:', API_BASE_URL);

// Listen for device ID sync events to ensure consistency across contexts
if (typeof window !== 'undefined') {
  // Listen for CustomEvent (same window)
  window.addEventListener('device-id-sync', ((event: CustomEvent) => {
    const { deviceId } = event.detail;
    try {
      if (cachedDeviceId !== deviceId) {
        logger.info('🔄 [APIClient] Syncing device ID from CustomEvent');
        localStorage.setItem('ctrl-enter-device-id', deviceId);
        cachedDeviceId = deviceId; // Update cache
      }
    } catch (error) {
      logger.error('❌ [APIClient] Failed to sync device ID:', error);
    }
  }) as EventListener);

  // Listen for postMessage (cross-iframe)
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'DEVICE_ID_SYNC' && event.data?.deviceId) {
      const { deviceId } = event.data;
      try {
        const currentDeviceId = localStorage.getItem('ctrl-enter-device-id');
        if (currentDeviceId !== deviceId) {
          logger.info('🔄 [APIClient] Syncing device ID from postMessage (cross-context)');
          localStorage.setItem('ctrl-enter-device-id', deviceId);
          cachedDeviceId = deviceId; // Update cache
        }
      } catch (error) {
        logger.error('❌ [APIClient] Failed to sync device ID from postMessage:', error);
      }
    }
  });

  // If we're in an iframe, request device ID from parent immediately
  const isInIframe = window.parent !== window;
  logger.info('🔍 [APIClient] Context check:', {
    isInIframe,
    parentExists: !!window.parent,
    windowSelf: window === window.self,
    location: window.location.href,
  });

  if (isInIframe) {
    logger.info('📨 [APIClient] Iframe detected, requesting device ID from parent');
    window.parent.postMessage({ type: 'REQUEST_DEVICE_ID' }, '*');
  } else {
    logger.info('🌐 [APIClient] Running in main window (not iframe)');
  }
}

/**
 * Cached device ID to avoid repeated localStorage access
 */
let cachedDeviceId: string | null = null;

/**
 * Clear device ID cache (used when session is invalid)
 */
export const clearDeviceId = (): void => {
  cachedDeviceId = null;
  try {
    localStorage.removeItem('ctrl-enter-device-id');
    logger.info('🗑️ [APIClient] Cleared device ID cache');
  } catch (error) {
    logger.error('❌ [APIClient] Failed to clear device ID:', error);
  }
};

/**
 * Get or create device ID for this browser
 * IMPORTANT: Uses a consistent key across all contexts (full app, extension sidebar, etc.)
 * to ensure the same device ID is used across the entire web client
 */
export const getDeviceId = (): string => {
  // CRITICAL DEBUG: Get stack trace to see WHO is calling this
  const stack = new Error().stack;
  const caller = stack?.split('\n')[2]?.trim() || 'unknown';

  // Return cached value if available
  if (cachedDeviceId) {
    logger.info('🔑 [APIClient] Returning CACHED device ID:', cachedDeviceId);
    logger.info('📍 [APIClient] Called from (cached):', caller);
    return cachedDeviceId;
  }

  // Use the same key as token storage to ensure consistency
  const DEVICE_ID_KEY = 'ctrl-enter-device-id';

  try {
    const existingId = localStorage.getItem(DEVICE_ID_KEY);

    // Debug: log ALL localStorage keys to see if old device-id exists
    const allKeys = Object.keys(localStorage);
    logger.info('🔍 [APIClient] localStorage keys:', allKeys);
    logger.info('🔍 [APIClient] device-id (old key):', localStorage.getItem('device-id'));
    logger.info('🔍 [APIClient] ctrl-enter-device-id (new key):', existingId);
    logger.info('📍 [APIClient] Called from (localStorage):', caller);

    if (existingId) {
      cachedDeviceId = existingId;
      logger.info('🔑 [APIClient] Using existing device ID from localStorage:', existingId);
      return existingId;
    }

    const deviceId = `web-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    localStorage.setItem(DEVICE_ID_KEY, deviceId);
    cachedDeviceId = deviceId;
    logger.info('🔑 [APIClient] Generated NEW device ID:', deviceId);
    logger.info('📍 [APIClient] Called from (new generation):', caller);

    // Broadcast to parent if in iframe
    if (window.parent !== window) {
      logger.info('📤 [APIClient] Broadcasting new device ID to parent');
      window.parent.postMessage(
        {
          type: 'DEVICE_ID_SYNC',
          deviceId,
        },
        '*'
      );
    }

    return deviceId;
  } catch (error) {
    const sessionDeviceId = `web-session-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    logger.warn('⚠️ [APIClient] localStorage unavailable, using session ID');
    cachedDeviceId = sessionDeviceId;
    return sessionDeviceId;
  }
};

/**
 * ts-rest React Query client for API calls
 */
export const tsrClient = initTsrReactQuery(apiContract, {
  baseUrl: API_BASE_URL,
  baseHeaders: {
    'x-app-source': 'enter-ctrl-web',
    // Note: x-device-id will be set dynamically in api() function
  },
  api: async args => {
    // Get access token
    let accessToken: string | null = null;

    try {
      accessToken = await getValidAccessToken();
    } catch (error) {
      logger.error('❌ [APIClient] Error getting access token:', error);
    }

    // Create headers
    const headers = new Headers(args.headers);

    // Set device ID dynamically (allows regeneration after session errors)
    headers.set('x-device-id', getDeviceId());

    if (accessToken) {
      // Use WorkOS access token for authenticated requests
      headers.set('Authorization', `Bearer ${accessToken}`);
    } else {
      // Fall back to public API key for unauthenticated requests
      headers.set('Authorization', `Bearer ${API_PUBLIC_KEY}`);
    }

    // Handle FormData - let browser set Content-Type
    if (args.body instanceof FormData) {
      headers.delete('Content-Type');
    }

    // Setup timeout
    const isStreamingEndpoint = args.headers['x-stream'] === 'true';
    const timeoutMs = isStreamingEndpoint ? 360000 : 60000;

    const abortController = new AbortController();
    const timeoutId = setTimeout(() => {
      logger.warn(`⏰ [APIClient] Request timeout after ${timeoutMs}ms`);
      abortController.abort();
    }, timeoutMs);

    const fetchOptions: RequestInit = {
      method: args.method,
      headers,
      signal: abortController.signal,
    };

    if (args.body != null) {
      fetchOptions.body = args.body;
    }

    let response: Response;
    try {
      response = await fetch(args.path, fetchOptions);
      clearTimeout(timeoutId);
    } catch (error) {
      clearTimeout(timeoutId);
      logger.error('❌ [APIClient] Fetch error:', error);
      throw new Error('NETWORK_ERROR');
    }

    // Handle streaming endpoints
    if (isStreamingEndpoint) {
      if (!response.ok) {
        try {
          const errorText = await response.text();
          let errorBody;
          try {
            errorBody = JSON.parse(errorText);
          } catch {
            errorBody = { error: errorText || `HTTP ${response.status}` };
          }

          return {
            status: response.status,
            body: errorBody,
            headers: response.headers,
          };
        } catch (parseError) {
          return {
            status: response.status,
            body: { error: `HTTP ${response.status}` },
            headers: response.headers,
          };
        }
      }

      // Return streaming response
      return response;
    }

    // Handle regular JSON responses
    try {
      const jsonData = await response.json();

      return {
        status: response.status,
        body: jsonData,
        headers: response.headers,
      };
    } catch (error) {
      logger.error('❌ [APIClient] Failed to parse JSON:', error);
      return {
        status: 500,
        body: { error: 'Failed to parse response' },
        headers: response.headers,
      };
    }
  },
});

export type TsrClient = typeof tsrClient;
